/**
 * The what/why slot of an expense description - the line's business purpose.
 *
 * Descriptions follow "who: <supplier>, what/why: <purpose>, when: <date>".
 * A claim cannot be submitted until every line says what was bought and why
 * after "what/why:". A description filled in from an invoice is a stub -
 * "who: Hotel X, what/why: , when: 12-03-2026" - and nothing reads the purpose
 * off a receipt, so the claimant has to write it. A line typed by hand, or one
 * whose "what/why:" label was deleted, needs one too: those used to pass with
 * no purpose at all. These helpers find such lines so the form can point at
 * them (and the submit check refuse them); the backend's
 * src/utils/expensePurpose.mjs applies the same rule.
 */

/** "what/why:" followed by nothing before the next comma or the end. */
export const EMPTY_WHAT_WHY = /what\/why:\s*(,|$)/i;

/** What a new line's description starts as, so the slot is always there. */
export const DESCRIPTION_TEMPLATE = 'who: , what/why: , when: ';

// What follows "what/why:", up to the ", when:" or ", who:" after it, or the
// end. Lazy, so a purpose that itself contains commas is kept whole.
const PURPOSE_SLOT = /what\/why:([\s\S]*?)(?:,\s*(?:who|when)\s*:|$)/i;

// A purpose has to say something - a letter or a digit, not just "-" or ".".
const SAYS_SOMETHING = /[\p{L}\p{N}]/u;

/** The text written after "what/why:", trimmed, or null when there is no such label. */
export const purposeText = (description) => {
  const match = PURPOSE_SLOT.exec(String(description ?? ''));
  return match ? match[1].trim() : null;
};

/** True when the description has a "what/why:" label to write the purpose after. */
export const hasPurposeSlot = (description) => purposeText(description) !== null;

/** True when a line does not yet say what was bought and why. */
export const needsPurpose = (description) => {
  const text = purposeText(description);
  return text === null || !SAYS_SOMETHING.test(text);
};

/**
 * A new line's template, never typed in - treated as blank, so a receipt read
 * into the line can still fill its who and when.
 */
export const isUntouchedTemplate = (description) =>
  String(description ?? '').replace(/\s+/g, ' ').trim() === DESCRIPTION_TEMPLATE.trim();

/**
 * The description with a "what/why: " label put back, for a line that has
 * none: before its ", when:" if it has one (where the template keeps it),
 * otherwise at the end - and the template itself when the description is
 * empty.
 */
export const withPurposeSlot = (description) => {
  const text = String(description ?? '').trimEnd();
  if (!text) return DESCRIPTION_TEMPLATE;
  if (hasPurposeSlot(text)) return text;
  const when = /,\s*when\s*:/i.exec(text);
  if (when) return `${text.slice(0, when.index)}, what/why: ${text.slice(when.index)}`;
  return `${text.replace(/,\s*$/, '')}, what/why: `;
};

/** True when the description still has its "when:" label. */
export const hasWhenLabel = (description) => /\bwhen\s*:/i.test(String(description ?? ''));

/**
 * The structure labels a description has lost. The claimant adds to the
 * structure or edits after each label, and never deletes a label (the user,
 * 2026-10-21). "what/why:" and "when:" only - "who:" is the supplier, read off
 * the receipt. An empty description has lost nothing: it is a line not yet
 * started, and putting the structure back gives it the whole template.
 */
export const missingStructureLabels = (description) => {
  if (!String(description ?? '').trim()) return [];
  return [
    !hasPurposeSlot(description) && 'what/why:',
    !hasWhenLabel(description) && 'when:',
  ].filter(Boolean);
};

/** The description with a deleted "what/why:" and/or "when:" label put back in place. */
export const withStructure = (description) => {
  const text = withPurposeSlot(description);
  if (hasWhenLabel(text)) return text;
  // Keep the space after a label the text ends on ("what/why: ").
  const base = /:\s*$/.test(text) ? `${text.trimEnd()} ` : text.replace(/[\s,]+$/, '');
  return `${base}, when: `;
};

/**
 * Where the caret goes to type the purpose: just after "what/why: ", before
 * the ", when:" that follows. -1 when the description has no such slot.
 */
export const purposeCaretIndex = (description) => {
  const match = /what\/why:[ \t]*/i.exec(String(description || ''));
  return match ? match.index + match[0].length : -1;
};

/** [{ id, line }] for every line (1-based, in form order) still missing its purpose. */
export const linesNeedingPurpose = (items) => (items || [])
  .map((item, index) => (needsPurpose(item?.expenditureDescription) ? { id: item.id, line: index + 1 } : null))
  .filter(Boolean);

/** The sentence a draft save appends, or '' when every line has its purpose. */
export const draftPurposeReminder = (items) => {
  const n = linesNeedingPurpose(items).length;
  if (n === 0) return '';
  return ` ${n} line${n === 1 ? ' still needs' : 's still need'} what was bought and why before you can submit.`;
};
